import { useTranslation } from "react-i18next";
import { useCheckAttendanceExceptionEligibilityMutation } from "@/services/(my-space)/api.time-attendance";
import { useLazyGetCheckInContextQuery } from "@/services/(company)/attendanceRulesV2Api";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { LogOut, LogIn, Clock, type LucideIcon } from "lucide-react";

import TimeAttendanceRuleModal from "@/components/time-attendance";
import { CheckInScopeSelector } from "@/components/attendance/CheckInScopeSelector";
import { QUICK_ACTIVITY_ICONS } from "@/pages/(home)/(company)/time-attendance/settings/quick-activity/_components/quickActivityIcons";
import type {
  IAttendanceExceptionEligibility,
  IPendingAttendanceException,
} from "@/types/(my-space)/timeAttendace.interface";
import type { ICheckInContext } from "@/types/(company)/attendanceRulesV2.interface";
import { useCurrentLocation } from "@/hooks/useCurrentLocation";
import { useLocale } from "@/hooks/useLocale";
import type { ILocationFix } from "@/utils/geolocation";

// Map buttonIcon string → lucide component
const BUTTON_ICON_MAP: Record<string, LucideIcon> = {
  logout: LogOut,
  login: LogIn,
};

function resolveIcon(iconName: string | undefined): LucideIcon | null {
  if (!iconName) return null;
  if (BUTTON_ICON_MAP[iconName]) return BUTTON_ICON_MAP[iconName];
  return QUICK_ACTIVITY_ICONS.find((i) => i.value === iconName)?.icon ?? null;
}

type Props = {
  dataLoading: boolean;
  isCurrentlyCheckedIn: boolean;
  buttonLabel?: string;
  buttonColor?: string;
  buttonIcon?: string;
  /** All of the caller's pending exceptions, oldest first. The oldest is the
   * only one HR can act on yet, but any of them blocks nothing here — new
   * clock attempts are still allowed and just queue their own exception. */
  pendingExceptions?: IPendingAttendanceException[];
};

export const CheckInCheckoutButton = ({
  isCurrentlyCheckedIn,
  dataLoading,
  buttonLabel,
  buttonColor,
  buttonIcon,
  pendingExceptions = [],
}: Props) => {
  const { t } = useTranslation("my-space");
  const { formatDate } = useLocale();
  const oldestPending = pendingExceptions[0] ?? null;
  const { locate, isLocating } = useCurrentLocation();

  const [timeAttendanceRuleModalOpen, setTimeAttendanceRuleModalOpen] =
    useState(false);
  const [eligibilityResponse, setEligibilityResponse] =
    useState<IAttendanceExceptionEligibility>();

  const [scopeSelectorOpen, setScopeSelectorOpen] = useState(false);
  const [pendingContext, setPendingContext] = useState<ICheckInContext | null>(
    null,
  );
  const [chosenScope, setChosenScope] = useState<{
    teamId?: number;
    projectId?: number;
  }>({});

  const [checkEligibilityAttendanceRule, { isLoading: checkInLoading }] =
    useCheckAttendanceExceptionEligibilityMutation();
  const [triggerCheckInContext, { isLoading: contextLoading }] =
    useLazyGetCheckInContextQuery();

  const runEligibility = async (
    location?: ILocationFix | null,
    teamId?: number,
    projectId?: number,
    locationId?: number,
  ) => {
    const toastId = "toggle-check-eligibility-toast";
    try {
      // Located at the moment of the action, after any scope selection, so the
      // fix reflects where the employee is now rather than on page load.
      const fix = location ?? (await locate());
      const res = await checkEligibilityAttendanceRule({
        lat: fix?.lat ?? null,
        long: fix?.long ?? null,
        accuracy: fix?.accuracy ?? null,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        teamId,
        projectId,
        locationId,
      }).unwrap();
      setTimeAttendanceRuleModalOpen(!res.data.isAllowedToPerformAction);
      setEligibilityResponse(res.data);
    } catch (e: any) {
      const message = e?.data?.message ?? e?.message ?? t("toasts.something_went_wrong");
      // Pending exception is an expected wait-state, not a failure — show as a warning, not an error.
      if (message.includes("pending attendance exception request")) {
        toast(message, { id: toastId, icon: "⚠️" });
      } else {
        toast.error(message, { id: toastId });
      }
    }
  };

  const advanceContext = async (scope: {
    teamId?: number;
    projectId?: number;
  }) => {
    try {
      const ctx = await triggerCheckInContext(scope).unwrap();
      if (ctx.needsSelection) {
        setPendingContext(ctx);
        setScopeSelectorOpen(true);
        return;
      }
      await runEligibility(null, scope.teamId, scope.projectId);
    } catch {
      await runEligibility(null, scope.teamId, scope.projectId);
    }
  };

  const handleCheckEligibilityAttendanceRule = async (
    location?: ILocationFix | null,
  ) => {
    try {
      setChosenScope({});
      const ctx = await triggerCheckInContext().unwrap();
      if (ctx.needsSelection) {
        setPendingContext(ctx);
        setScopeSelectorOpen(true);
        return;
      }
    } catch {
      // If context endpoint fails, fall through to eligibility — BE will still resolve via default rule.
    }
    await runEligibility(location);
  };

  // The backend alternates a new pending exception's direction off the last
  // PENDING one (see attendance-exceptions.service.ts) so two check-ins can't
  // stack in a row. Mirror that here: once approved, the newest pending
  // exception is what `isCurrentlyCheckedIn` will become — so the button
  // should already show that next state, not the stale confirmed one.
  const newestPending = pendingExceptions.at(-1) ?? null;
  const effectiveCheckedIn = newestPending
    ? newestPending.requestType === "check_in"
    : isCurrentlyCheckedIn;

  // The API sends `buttonLabel` in English. Translate it when it is one of the
  // known defaults; a company's genuinely custom label passes through as-is.
  const DEFAULT_LABELS: Record<string, string> = {
    "check in": "labels.check_in",
    "check-in": "labels.check_in",
    "check out": "ui.check_out_2",
    "check-out": "ui.check_out_2",
  };
  const fallbackLabel = effectiveCheckedIn
    ? t("ui.check_out_2")
    : t("labels.check_in");
  // A pending exception makes the API's own label stale, so it is ignored.
  const apiLabel = oldestPending ? undefined : buttonLabel;
  const apiLabelKey = apiLabel
    ? DEFAULT_LABELS[apiLabel.trim().toLowerCase()]
    : undefined;
  const label = apiLabelKey ? t(apiLabelKey) : (apiLabel ?? fallbackLabel);
  const colorRaw = buttonColor ? buttonColor.replace(/\s+/g, ", ") : null;
  const Icon = resolveIcon(buttonIcon);

  // Only apply custom color when API sends one that differs from default check-in/out
  const isCustomColor = !oldestPending && !!colorRaw && !!buttonColor;

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Button
          className={cn(
            "shrink-0 transition-all duration-200",
            isCustomColor &&
              "bg-[rgba(var(--c),0.1)] hover:bg-[rgba(var(--c),0.18)] border-[rgb(var(--c))] text-[rgb(var(--c))]",
            // A pending exception makes this button's state provisional —
            // amber instead of the normal green/red so it reads as "not
            // confirmed yet" while remaining fully clickable.
            oldestPending &&
              "border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100",
          )}
          onClick={() => handleCheckEligibilityAttendanceRule()}
          disabled={checkInLoading || contextLoading || isLocating}
          isLoading={
            checkInLoading || contextLoading || isLocating || dataLoading
          }
          variant={
            oldestPending
              ? "outline"
              : isCurrentlyCheckedIn
                ? "outline-danger"
                : "outline-success"
          }
          style={
            isCustomColor ? ({ "--c": colorRaw } as CSSProperties) : undefined
          }
          title={
            oldestPending
              ? t("common:shared_ui.pending_exception_toggle_hint", {
                  time: oldestPending.attemptedTime
                    ? formatDate(new Date(oldestPending.attemptedTime), "p")
                    : t("common:shared_ui.the_original_attempt_time"),
                })
              : undefined
          }
        >
          {oldestPending ? <Clock size={14} /> : Icon && <Icon size={14} />}
          {label}
        </Button>

        {oldestPending && (
          <Link
            to="/time-attendance/attendance-exceptions?status=pending"
            className="inline-flex items-center rounded-md border border-amber-400 bg-amber-50 px-1.5 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100"
            title={t("common:shared_ui.pending_clock_attempts_hint", {
              count: pendingExceptions.length,
            })}
          >
            {t("common:shared_ui.n_pending", {
              count: pendingExceptions.length,
            })}
          </Link>
        )}
      </div>

      <TimeAttendanceRuleModal
        open={timeAttendanceRuleModalOpen}
        onClose={() => setTimeAttendanceRuleModalOpen(false)}
        eligibility={eligibilityResponse}
        handleCheckEligibilityAttendanceRule={
          handleCheckEligibilityAttendanceRule
        }
      />

      <CheckInScopeSelector
        open={scopeSelectorOpen}
        context={pendingContext}
        onCancel={() => {
          setScopeSelectorOpen(false);
          setPendingContext(null);
          setChosenScope({});
        }}
        onConfirm={async (sel) => {
          setScopeSelectorOpen(false);
          setPendingContext(null);
          if (sel.locationId != null) {
            // Stage 2 complete — fire eligibility with full selection.
            await runEligibility(
              null,
              chosenScope.teamId,
              chosenScope.projectId,
              sel.locationId,
            );
            setChosenScope({});
            return;
          }
          // Stage 1 complete — advance to potential stage 2 (location).
          const next = {
            teamId: sel.teamId,
            projectId: sel.projectId,
          };
          setChosenScope(next);
          await advanceContext(next);
        }}
      />
    </>
  );
};
